import { Button, Chip, Divider, Typography } from "@mui/material";
import SidebarCard from "./Components/SidebarCard";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CampaignIcon from "@mui/icons-material/Campaign";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Vocabulary } from "../../Utils/Vocabulary";
import { localUrlEnum } from "../../Utils/urlEnum";
import DisplayField from "./Components/DisplayField";
import styles from "./Components/Components.module.css";

export interface LeftSidebarProps {
  lead: any;
  isLeadUri: boolean;
}

export default function LeftSidebar({
  lead,
  isLeadUri,
}: LeftSidebarProps) {
  const client = lead.client;
  const displayName = client
    ? [client.lastName || client.last_name, client.firstName || client.first_name].filter(Boolean).join(" ") || lead.name
    : lead.name;
  const displayPhone = client?.phone || lead.phone;
  const displayEmail = client?.email || lead.email;

  const vehicleName = lead.vehicle
    ? [lead.vehicle.brand_r?.nume, lead.vehicle.model_r?.nume]
        .filter(Boolean)
        .join(" ")
    : [lead.brand, lead.model].filter(Boolean).join(" ");
  const vehicleVin = lead.vehicle?.vin ?? lead.vin;

  const utmTags = lead.advertising_data
    ? [
        { prefix: "Source", value: lead.advertising_data.utm_source },
        { prefix: "Medium", value: lead.advertising_data.utm_medium },
        { prefix: "Campaign", value: lead.advertising_data.utm_campaign },
      ].filter(({ value }) => Boolean(value))
    : [];

  const adParams = lead.advertising_data
    ? [
        { label: Vocabulary.adName, value: lead.advertising_data.ad_set },
        { label: Vocabulary.campaignName, value: lead.advertising_data.campaign_name },
      ].filter(({ value }) => Boolean(value))
    : [];

  const additionalParams = lead.additional_params
    ? [
        {
          label: Vocabulary.whenPurchase,
          value: lead.additional_params.cand_doresti_achizitia?.replace(/_/g, " "),
        },
        {
          label: Vocabulary.howPurchase,
          value: lead.additional_params.cum_doresti_achizitia?.replace(/_/g, " "),
        },
        {
          label: Vocabulary.allocatedAdvance,
          value: lead.additional_params.avans_alocat?.replace(/_/g, " "),
        },
        {
          label: Vocabulary.purchaseQuality,
          value: lead.additional_params.calitate_achizitie?.replace(/_/g, " "),
        },
        {
          label: Vocabulary.companyCUI,
          value: lead.additional_params.cui_companie,
        },
        {
          label: Vocabulary.wantsConsultantContact,
          value: lead.additional_params.doreste_contact_consultant,
        },
        { label: Vocabulary.platform, value: lead.additional_params.platform },
      ].filter(({ value }) => Boolean(value))
    : [];

  return (
    <div className={styles.sidebar}>
      {/* Customer info */}
      <SidebarCard
        title={Vocabulary.client}
        icon={<PersonIcon fontSize="small" />}
        color="#3b7dd8"
        badge={client && (
          <Chip
            icon={<CheckCircleIcon style={{ fontSize: 13 }} />}
            label={Vocabulary.convertedToClient}
            size="small"
            style={{
              backgroundColor: "#dcfce7",
              color: "#15803d",
              fontWeight: 600,
              fontSize: 12,
              height: 26,
              borderRadius: 999,
            }}
          />
        )}
      >
        <DisplayField
          label={Vocabulary.name}
          value={displayName || ""}
        />
        <Divider className={styles.dividerSm} />
        <DisplayField
          label={Vocabulary.phone}
          value={displayPhone || ""}
          icon={<PhoneIcon style={{ fontSize: 16 }} />}
        />
        <DisplayField
          label={Vocabulary.email}
          value={displayEmail || ""}
          icon={<EmailIcon style={{ fontSize: 16 }} />}
        />

        {client && (
          <>
            {(client.street || client.city || client.country) && (
              <>
                <Divider className={styles.dividerSm} />
                <Typography variant="caption" color="text.disabled" style={{ padding: "4px 4px 0", display: "block", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {Vocabulary.address || "Adresă"}
                </Typography>
                <Typography variant="body2" style={{ padding: "2px 4px", fontSize: 12 }}>
                  {[client.street, client.city, client.country].filter(Boolean).join(", ")}
                </Typography>
              </>
            )}
            {client.isLegalPerson || client.is_legal_person ? (
              <>
                <Divider className={styles.dividerSm} />
                {(client.companyName || client.company_name) && (
                  <Typography variant="body2" style={{ padding: "2px 4px", fontWeight: 600, fontSize: 12 }}>
                    {client.companyName || client.company_name}
                  </Typography>
                )}
                {(client.CUI || client.cui) && (
                  <Typography variant="caption" color="text.secondary" style={{ padding: "0 4px" }}>
                    CUI: {client.CUI || client.cui}
                  </Typography>
                )}
                {(client.nrRegCom || client.nr_reg_com) && (
                  <Typography variant="caption" color="text.secondary" style={{ padding: "0 4px", display: "block" }}>
                    Nr. Reg. Com: {client.nrRegCom || client.nr_reg_com}
                  </Typography>
                )}
              </>
            ) : (
              client.CNP || client.cnp ? (
                <>
                  <Divider className={styles.dividerSm} />
                  <Typography variant="caption" color="text.secondary" style={{ padding: "2px 4px", display: "block" }}>
                    CNP: {client.CNP || client.cnp}
                  </Typography>
                </>
              ) : null
            )}
          </>
        )}
      </SidebarCard>

      {/* Vehicle of interest */}
      {(lead.brand || lead.vin || lead.vehicul_id || lead.vehicle) && (
        <SidebarCard
          title={Vocabulary.vehicleInterest}
          icon={<DirectionsCarIcon fontSize="small" />}
          color="#0d9488"
        >
          {vehicleName && (
            <Typography
              variant="body2"
              fontWeight={600}
              className={styles.inlinePadded}
            >
              {vehicleName}
            </Typography>
          )}
          {vehicleVin && (
            <Typography
              variant="caption"
              color="text.secondary"
              style={{ padding: "0 4px", display: "block", marginBottom: lead.vehicle ? 8 : 0 }}
            >
              {Vocabulary.vin}: {vehicleVin}
            </Typography>
          )}
          {lead.vehicle && (
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={<DirectionsCarIcon style={{ fontSize: 14 }} />}
              style={{ textTransform: "none", fontSize: 12, color: "#fff", marginTop: 4 }}
              onClick={() => window.open(`${localUrlEnum.vehicles}/vehicleDetails/${lead.vehicle.id}`, "_blank")}
            >
              {Vocabulary.goToVehicle}
            </Button>
          )}
        </SidebarCard>
      )}
      {/* Original message */}
      {lead.message && (
        <SidebarCard title={Vocabulary.message} color="#8b5cf6">
          <Typography
            variant="body2"
            color="text.secondary"
            className={styles.messageText}
          >
            "{lead.message}"
          </Typography>
        </SidebarCard>
      )}
      {/* Lead source */}
      <SidebarCard
        title={Vocabulary.leadSource}
        icon={<CampaignIcon fontSize="small" />}
        color="#f59e0b"
      >
        <div className={styles.sourceParams}>
          {/* Source */}
          <div>
            <Typography variant="caption" color="text.disabled" display="block">
              {isLeadUri ? "Lead" : Vocabulary.source || "Sursă"}
            </Typography>
            <div style={{ marginTop: 4 }}>
              <Chip label={lead.source || "—"} size="small" className={styles.leadChip} />
            </div>
          </div>

          {/* Additional params */}
          {additionalParams.map(({ label, value }) => (
            <div key={label}>
              <Typography variant="caption" color="text.disabled" display="block">
                {label}
              </Typography>
              <Typography variant="body2" className={styles.paramValue}>
                {value}
              </Typography>
            </div>
          ))}

          {/* Ad / campaign name */}
          {adParams.map(({ label, value }) => (
            <div key={label}>
              <Typography variant="caption" color="text.disabled" display="block">
                {label}
              </Typography>
              <Typography variant="body2" className={styles.paramValue}>
                {value}
              </Typography>
            </div>
          ))}

          {/* UTM tags */}
          {utmTags.length > 0 && (
            <div className={styles.utmList}>
              {utmTags.map(({ prefix, value }) => (
                <Chip key={prefix} size="small" variant="outlined" label={`${prefix}: ${value}`} className={styles.utmChip} />
              ))}
            </div>
          )}
        </div>
      </SidebarCard>
    </div>
  );
}
